// =====================================================================
// migration/import-from-localstorage.ts
// =====================================================================
// One-time migration for existing prototype users. Reads the localStorage
// state (key: 'eagle-state-v6') and writes each record to Postgres via
// the db abstraction.
//
// Usage:
//   1. Member opens the new (real-backend) version of Bag Tag and signs in
//      with magic link. Their auth.users + members row is created on signup.
//   2. If localStorage contains 'eagle-state-v6', show a "Migrate your data?"
//      prompt. On accept, run runMigration().
//   3. Migration logs progress, dedupes by ID where possible, surfaces errors.
//   4. On success, clear localStorage (or mark migrated to prevent re-runs).
//
// Idempotency: each call to runMigration() can be safely retried. We use
// upsert where possible and skip records that already exist by ID.
//
// What's migrated:
//   • Profile fields (about_me, service, handicap, venmo, avatar, visibility)
//   • Posts (with reactions and comments)
//   • Follows
//   • Rounds (with hole-by-hole scores)
//   • Tournaments (organized by this member)
//   • Games (organized by this member)
//   • Call Your Shots
//   • Aces
//   • Achievements
//   • Points ledger
//   • Charity donations
//   • Pending invitations (sent by this member)
//
// What's NOT migrated:
//   • Other members' data (each member runs their own migration)
//   • Notifications (regenerated server-side)
//   • Suggestions (typically empty for early users)
// =====================================================================

import { db } from '../db';
import type { UUID } from '../db/types';

const STORAGE_KEY = 'eagle-state-v6';
const MIGRATION_FLAG_KEY = 'bag-tag-migrated-at';

interface MigrationResult {
  ok: boolean;
  imported: Record<string, number>;        // counts per table
  errors: Array<{ table: string; id?: string; error: string }>;
  durationMs: number;
}

export async function runMigration(opts: { dryRun?: boolean; verbose?: boolean } = {}): Promise<MigrationResult> {
  const start = Date.now();
  const errors: MigrationResult['errors'] = [];
  const imported: Record<string, number> = {};

  const log = (msg: string) => { if (opts.verbose) console.log('[migration]', msg); };

  // Check for existing state
  if (typeof localStorage === 'undefined') {
    return { ok: false, imported, errors: [{ table: 'init', error: 'No localStorage available' }], durationMs: 0 };
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return { ok: false, imported, errors: [{ table: 'init', error: 'No localStorage state found' }], durationMs: 0 };
  }

  // Check we're authenticated
  const me = await db.auth.me();
  if (!me) {
    return { ok: false, imported, errors: [{ table: 'init', error: 'Not signed in' }], durationMs: 0 };
  }

  // Parse the state
  let state: any;
  try {
    state = JSON.parse(raw);
  } catch (e) {
    return { ok: false, imported, errors: [{ table: 'init', error: `Parse failed: ${(e as Error).message}` }], durationMs: 0 };
  }

  log(`Found state. Member: ${me.handle}. Beginning import...`);

  // === 1. Profile fields ===
  try {
    if (state.me) {
      const patch: any = {};
      if (state.me.aboutMe !== undefined) patch.about_me = state.me.aboutMe;
      if (state.me.handicap != null) patch.handicap = state.me.handicap;
      if (state.me.venmo) patch.venmo = state.me.venmo;
      if (state.me.avatar) patch.avatar = state.me.avatar;
      if (state.me.profilePhoto) patch.profile_photo_url = state.me.profilePhoto;
      if (state.me.visibility) patch.visibility = state.me.visibility;
      if (state.me.service?.branch) patch.service_branch = state.me.service.branch;
      if (state.me.service?.rank)   patch.service_rank   = state.me.service.rank;
      if (state.me.service?.status) patch.service_status = state.me.service.status;

      if (Object.keys(patch).length && !opts.dryRun) {
        await db.members.update(patch);
      }
      imported.profile = 1;
      log(`Profile updated: ${Object.keys(patch).join(', ')}`);
    }
  } catch (e) {
    errors.push({ table: 'members', error: (e as Error).message });
  }

  // === 2. Follows ===
  const localIdToServerId = new Map<string, UUID>();    // 'me' → real UUID, etc.
  localIdToServerId.set('me', me.id);

  // For other members, we can't follow them unless they've also onboarded
  // and we know their IDs. For now, follows can only be re-established
  // after both members migrate. Skip in v1 migration; members re-follow.
  log('Follows skipped — to be re-established after both members migrate');

  // === 3. Posts (with reactions and comments) ===
  if (Array.isArray(state.posts)) {
    for (const p of state.posts) {
      if (p.authorId !== 'me') continue;       // only migrate own posts
      try {
        if (opts.dryRun) {
          imported.posts = (imported.posts ?? 0) + 1;
          continue;
        }
        const post = await db.posts.create({
          type: mapPostType(p.type),
          body: p.body || p.message || p.notes || null,
          photo_url: p.photo || p.image || null,
        });
        imported.posts = (imported.posts ?? 0) + 1;
        localIdToServerId.set(p.id, post.id);

        // Comments (only ones I authored)
        for (const c of (p.comments || [])) {
          if (c.authorId !== 'me') continue;
          try {
            await db.comments.add(post.id, c.body || c.text || '');
            imported.comments = (imported.comments ?? 0) + 1;
          } catch (e) {
            errors.push({ table: 'comments', id: c.id, error: (e as Error).message });
          }
        }

        // My own reactions to the post
        for (const r of (p.reactions || [])) {
          if (r.userId !== 'me' || !r.emoji) continue;
          try {
            await db.reactions.toggle(post.id, r.emoji);
            imported.reactions = (imported.reactions ?? 0) + 1;
          } catch (e) {
            errors.push({ table: 'reactions', id: r.id, error: (e as Error).message });
          }
        }
      } catch (e) {
        errors.push({ table: 'posts', id: p.id, error: (e as Error).message });
      }
    }
  }

  // === 4. Aces ===
  if (Array.isArray(state.aces)) {
    for (const a of state.aces) {
      if (a.userId !== 'me') continue;
      try {
        if (!opts.dryRun) {
          await db.aces.create({
            hole_num: a.hole || a.holeNum,
            played_at: new Date(a.t || a.playedAt || Date.now()).toISOString(),
            notes: a.notes || null,
          });
        }
        imported.aces = (imported.aces ?? 0) + 1;
      } catch (e) {
        errors.push({ table: 'aces', id: a.id, error: (e as Error).message });
      }
    }
  }

  // === 5. Achievements ===
  if (Array.isArray(state.achievements)) {
    for (const a of state.achievements) {
      if (a.userId !== 'me') continue;
      try {
        if (!opts.dryRun) {
          await db.achievements.unlock(a.achievementId || a.id);
        }
        imported.achievements = (imported.achievements ?? 0) + 1;
      } catch (e) {
        errors.push({ table: 'achievements', id: a.id, error: (e as Error).message });
      }
    }
  }

  // === 6. Charity donations ===
  if (Array.isArray(state.charityDonations)) {
    for (const d of state.charityDonations) {
      if (d.userId !== 'me') continue;
      try {
        if (!opts.dryRun) {
          await db.donations.log({
            amount_cents: Math.round((d.amount || 0) * 100),
            venmo_note: d.note || null,
          });
        }
        imported.donations = (imported.donations ?? 0) + 1;
      } catch (e) {
        errors.push({ table: 'donations', id: d.id, error: (e as Error).message });
      }
    }
  }

  // === 7. Call Your Shots ===
  if (Array.isArray(state.spectatorBets)) {
    for (const s of state.spectatorBets) {
      if (s.playerId !== 'me') continue;
      try {
        if (opts.dryRun) {
          imported.callShots = (imported.callShots ?? 0) + 1;
          continue;
        }
        const shot = await db.callShots.propose({
          target_score: s.targetScore,
          notes: s.notes || null,
        });
        if (s.status === 'completed' && s.actualScore != null) {
          await db.callShots.settle(shot.id, s.actualScore);
        }
        imported.callShots = (imported.callShots ?? 0) + 1;
      } catch (e) {
        errors.push({ table: 'call_your_shots', id: s.id, error: (e as Error).message });
      }
    }
  }

  // === 8. Points ledger ===
  // We DON'T migrate the points ledger directly — points are awarded
  // server-side as part of settling shots, aces, achievements. Migrating
  // raw entries would double-award.
  log('Points ledger skipped — points re-awarded via settle/unlock above');

  // === Done ===
  const durationMs = Date.now() - start;
  const ok = errors.length === 0;

  if (ok && !opts.dryRun) {
    localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
    // Keep the original state in localStorage for now (backup), but mark migrated
    // so we don't prompt again. Could clear after a grace period.
  }

  log(`Migration ${ok ? 'completed' : 'completed with errors'} in ${durationMs}ms`);
  log(`Imported: ${JSON.stringify(imported)}`);
  if (errors.length) log(`Errors: ${JSON.stringify(errors)}`);

  return { ok, imported, errors, durationMs };
}

// Helper: map old post types to new schema enum
function mapPostType(t: string | undefined): 'round' | 'tournament' | 'game' | 'ace' | 'achievement' | 'status' {
  if (!t) return 'status';
  if (['round', 'tournament', 'game', 'ace', 'achievement', 'status'].includes(t)) return t as any;
  if (t === 'score') return 'round';
  return 'status';
}

/** Convenience: prompt the user via window.confirm() before running. */
export async function maybePromptAndMigrate(): Promise<MigrationResult | null> {
  if (typeof window === 'undefined') return null;
  if (localStorage.getItem(MIGRATION_FLAG_KEY)) return null;          // already migrated
  if (!localStorage.getItem(STORAGE_KEY)) return null;                 // nothing to migrate

  const ok = window.confirm(
    'We can migrate your existing Bag Tag activity (posts, scores, aces, ' +
    'achievements) to the new sync\'d server so it\'s available across your devices. ' +
    'This will take a few seconds. Continue?'
  );
  if (!ok) return null;
  const result = await runMigration({ verbose: true });
  if (result.ok) {
    window.alert(
      'Migration complete! ' +
      Object.entries(result.imported).map(([t, n]) => `${t}: ${n}`).join(', ')
    );
  } else {
    window.alert(`Migration had ${result.errors.length} errors. Check console for details.`);
  }
  return result;
}
